// src/layouts/Layout.tsx

import { AppBar, Box, LinearProgress, Toolbar, useTheme } from '@mui/material'
import { useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { KARMA_Route } from '@/types/karma/route'

import { getStorage, SnackbarBridge } from '../api'
import {
  CopyrightNotice,
  DlgSettingsGlobal,
  FolderSelector,
  HStack,
  OperatorMenu,
  ProviderWrapper,
  UpdateAvailable,
  usePreloader,
} from '../components/'
import routes from '../routes/routes'

import { Sidebar } from './Sidebar'

type RouteSwitcherProps = {
  routes: KARMA_Route[]
}

const RouteSwitcher = ({ routes }: RouteSwitcherProps) => (
  <Routes>
    {routes.map(route =>
      route.redirect ? (
        <Route
          element={<Navigate to={route.pathTo} replace />}
          key={route.path}
          path={route.path}
        />
      ) : (
        <Route
          element={<route.component />}
          key={route.path}
          path={route.path}
        />
      )
    )}
  </Routes>
)

const TopPreloader = () => {
  const { isLoading, percentage } = usePreloader()

  return isLoading ? (
    <LinearProgress
      color={'primary'}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        zIndex: 9999,
      }}
      value={percentage}
      variant={'determinate'}
    />
  ) : null
}

export const Layout = ({ ...rest }) => {
  const theme = useTheme()
  const bgcolor = theme.palette.white

  const NOT_LOGGED_IN = getStorage('user') === null

  const [collapsed, setCollapsed] = useState(false)

  const SIDEBAR_EXPANDED = 260
  const SIDEBAR_COLLAPSED = 72

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED

  return NOT_LOGGED_IN ? (
    <Navigate to={'/pages/login-page'} replace />
  ) : (
    <ProviderWrapper>
      <TopPreloader />
      <SnackbarBridge />
      {/* einstellungen */}
      <DlgSettingsGlobal />
      {/* einstellungen end */}

      <Sidebar {...rest} collapsed={collapsed} setCollapsed={setCollapsed} />

      <Box
        sx={{
          float: 'right',
          maxHeight: '100%',
          overflow: 'auto',
          overflowScrolling: 'touch',
          position: 'relative',
          width: '100%',
          [theme.breakpoints.up('md')]: {
            width: `calc(100% - ${sidebarWidth}px)`,
          },
        }}
      >
        <AppBar
          elevation={0}
          sx={{
            bgcolor,
            pl: `${sidebarWidth}px`,
            borderBottom: 'solid 1px #999',
          }}
        >
          <Toolbar
            sx={{
              minHeight: '50px',
              px: '15px',
              mx: 'auto',
              '&:before, &:after': { display: 'table', content: '" "' },
              '&:after': { clear: 'both' },
              [theme.breakpoints.down('xs')]: { px: '0px !important' },
              width: '95%',
              alignItems: 'center',
              color: 'black',
              display: 'flex',
              justifyContent: 'space-between',
              '& > button': { color: 'black' },
              '& > div > button': { color: 'black' },
            }}
          >
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <FolderSelector />
            </Box>

            <HStack spacing={3}>
              <UpdateAvailable />
              <OperatorMenu />
            </HStack>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            '&:before, &:after': { display: 'table', content: '" "' },
            '&:after': { clear: 'both' },
            mx: 0,
            mt: '75px',
            //minHeight: '50px',
            minHeight: 'calc(100vh - 120px)',
            px: 0,
            [theme.breakpoints.down('xs')]: {
              m: '5px !important',
              mt: '80px !important',
              p: '30px 0px !important',
            },
          }}
        >
          <RouteSwitcher routes={routes} />
        </Box>

        <CopyrightNotice sx={{ p: 1, textAlign: 'center' }} />
      </Box>
    </ProviderWrapper>
  )
}
